import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getCategoriesWithProductCount } from "@/modules/product/product.service";
import { setShopFilter, clearShopFilters } from "@/modules/shop/shop.slice";
import { ChevronDown, ChevronUp } from "lucide-react";
import { usePathname } from "next/navigation";

interface ShopSidebarProps {
  isDrawer?: boolean;
  onClose?: () => void;
  isCostToCost?: boolean;
  isOthersProduct?: boolean;
}

export const ShopSidebar: React.FC<ShopSidebarProps> = ({
  isDrawer = false,
  onClose,
  isCostToCost: propIsCostToCost,
  isOthersProduct: propIsOthersProduct,
}) => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { filters } = useAppSelector((state) => state.shop);
  const { CategoryWithCountList } = useAppSelector((state) => state.category);

  const isCostToCost =
    propIsCostToCost ??
    (pathname?.includes("/cost-to-cost") || false);
  const isOthersProduct =
    propIsOthersProduct ??
    (pathname?.includes("/other-products") || false);
  const isOnline = Boolean(filters.IsOnline);

  const [localPrice, setLocalPrice] = useState({
    min: 0,
    max: 100000,
  });

  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(true);
  const lastFetchedParams = useRef<string | null>(null);

  useEffect(() => {
    if (isDrawer) return;

    const paramsKey = JSON.stringify({
      IsOnline: isOnline,
      IsCostToCost: isCostToCost,
      IsOthersProduct: isOthersProduct,
    });

    if (lastFetchedParams.current === paramsKey) {
      return;
    }
    lastFetchedParams.current = paramsKey;

    dispatch(
      getCategoriesWithProductCount({
        IsOnline: isOnline,
        IsCostToCost: isCostToCost,
        IsOthersProduct: isOthersProduct,
      })
    );
  }, [dispatch, isOnline, isCostToCost, isOthersProduct, isDrawer]);

  const handleToggleCategory = (catID: number) => {
    const currentCats = filters.CategoryIDs
      ? filters.CategoryIDs.split(",").map(Number)
      : [];
    const newCats = currentCats.includes(catID)
      ? currentCats.filter((id) => id !== catID)
      : [...currentCats, catID];

    dispatch(setShopFilter({ CategoryIDs: newCats.join(",") }));
  };

  const handlePriceApply = () => {
    dispatch(
      setShopFilter({
        PriceFilter: `BETWEEN ${localPrice.min} AND ${localPrice.max}`,
      }),
    );
    if (isDrawer && onClose) {
      onClose();
    }
  };

  const handleClearAll = () => {
    dispatch(clearShopFilters());
    setLocalPrice({ min: 0, max: 100000 });
  };

  const isCategorySelected = (catID: number) => {
    return filters.CategoryIDs?.split(",").includes(String(catID));
  };

  const materials = ["Silver", "Gold", "AD", "Black Bead"];

  const containerClasses = isDrawer
    ? "w-full space-y-6"
    : "w-full lg:w-64 bg-black-2 border-r border-border-1 p-8 sticky top-[76px] h-[calc(100vh-76px)] overflow-y-auto custom-scrollbar";

  const Component = isDrawer ? "div" : "aside";

  return (
    <Component className={containerClasses}>
      <div className="flex justify-between items-center mb-8">
        <h3 className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold-core">
          Filters
        </h3>
        {(filters.CategoryIDs ||
          filters.PriceFilter ||
          filters.ProductName) && (
          <button
            onClick={handleClearAll}
            className="text-[9px] font-bold text-text-3 hover:text-white uppercase tracking-widest transition-colors cursor-pointer"
          >
            Clear All
          </button>
        )}
      </div>

      <section className="mb-8">
        <h4 className="text-[9px] font-bold tracking-[0.15em] uppercase text-text-3 mb-4 border-b border-border-1 pb-2">
          Product Type
        </h4>
        <div className="space-y-1">
          {[
            { label: "All", value: false },
            { label: "Online", value: true },
          ].map((type) => (
            <div
              key={type.label}
              onClick={() => dispatch(setShopFilter({ IsOnline: type.value }))}
              className="flex items-center gap-2.5 py-1.5 group cursor-pointer"
            >
              <div
                className={`w-3.5 h-3.5 border transition-all flex items-center justify-center ${filters.IsOnline === type.value ? "bg-gold-core border-gold-dim" : "border-border-1 bg-black group-hover:border-gold-dim"}`}
              >
                {filters.IsOnline === type.value && (
                  <span className="text-black text-[10px]">✓</span>
                )}
              </div>
              <label
                className={`text-xs transition-colors cursor-pointer ${filters.IsOnline === type.value ? "text-gold-core" : "text-text-2 group-hover:text-text-1"}`}
              >
                {type.label}
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <div
          className="flex justify-between items-center mb-4 border-b border-border-1 pb-2 cursor-pointer group"
          onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
        >
          <h4 className="text-[9px] font-bold tracking-[0.15em] uppercase text-text-3 group-hover:text-text-1 transition-colors">
            Categories
          </h4>
          {isCategoriesExpanded ? (
            <ChevronUp
              size={14}
              className="text-text-3 group-hover:text-text-1 transition-colors"
            />
          ) : (
            <ChevronDown
              size={14}
              className="text-text-3 group-hover:text-text-1 transition-colors"
            />
          )}
        </div>

        {isCategoriesExpanded && (
          <div className="space-y-1">
            {CategoryWithCountList.map((cat) => (
              <div
                key={cat.CategoryID}
                onClick={() => handleToggleCategory(cat.CategoryID)}
                className="flex items-center gap-2.5 py-1.5 group cursor-pointer"
              >
                <div
                  className={`w-3.5 h-3.5 border transition-all flex items-center justify-center ${isCategorySelected(cat.CategoryID) ? "bg-gold-core border-gold-dim" : "border-border-1 bg-black group-hover:border-gold-dim"}`}
                >
                  {isCategorySelected(cat.CategoryID) && (
                    <span className="text-black text-[10px]">✓</span>
                  )}
                </div>
                <label
                  className={`text-xs transition-colors cursor-pointer flex-1 flex justify-between items-center ${isCategorySelected(cat.CategoryID) ? "text-gold-core" : "text-text-2 group-hover:text-text-1"}`}
                >
                  <span>{cat.CategoryName}</span>
                  <span className="text-[9px] opacity-60 font-medium tabular-nums px-1.5 py-0.5 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                    {cat.ProductCount}
                  </span>
                </label>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Keeping Material as placeholder ui if backend supports in future */}
      {/* <section className="mb-8 opacity-50 pointer-events-none">
        <h4 className="text-[9px] font-bold tracking-[0.15em] uppercase text-text-3 mb-4 border-b border-border-1 pb-2">
          Material (Soon)
        </h4>
        <div className="space-y-1">
          {materials.map((mat) => (
            <div
              key={mat}
              className="flex items-center gap-2.5 py-1.5 group cursor-not-allowed"
            >
              <div className="w-3.5 h-3.5 border border-border-1 bg-black" />
              <label className="text-xs text-text-2">{mat}</label>
            </div>
          ))}
        </div>
      </section> */}

      <section>
        <h4 className="text-[9px] font-bold tracking-[0.15em] uppercase text-text-3 mb-4 border-b border-border-1 pb-2">
          Price Range (₹)
        </h4>
        <div className="flex gap-2 mb-4">
          <input
            type="number"
            className="bg-black-3 border border-border-1 text-text-1 px-2.5 py-2 text-xs w-full outline-none focus:border-gold-dim"
            placeholder="Min"
            value={localPrice.min}
            onChange={(e) =>
              setLocalPrice({ ...localPrice, min: Number(e.target.value) })
            }
          />
          <input
            type="number"
            className="bg-black-3 border border-border-1 text-text-1 px-2.5 py-2 text-xs w-full outline-none focus:border-gold-dim"
            placeholder="Max"
            value={localPrice.max}
            onChange={(e) =>
              setLocalPrice({ ...localPrice, max: Number(e.target.value) })
            }
          />
        </div>
        <button
          onClick={handlePriceApply}
          className="bg-gold-core text-black w-full py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-gold-bright transition-all cursor-pointer"
        >
          Apply Price
        </button>
      </section>
    </Component>
  );
};
